// frontend/packages/business-console/src/components/media/BulkTagDialog.jsx
//
// Add tags to every selected asset at once. Tags are merged into each asset's
// existing tags (never replaced), so a bulk edit can't wipe per-asset tags.

import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';

export default function BulkTagDialog({ open, count, tagOptions = [], onClose, onApply }) {
  const [tags, setTags] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setTags([]);
  }, [open]);

  const apply = async () => {
    setSaving(true);
    try {
      await onApply?.(tags);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 900 }}>
        Add tags to {count} {count === 1 ? 'asset' : 'assets'}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
          These are added to each asset&apos;s existing tags.
        </Typography>
        <Autocomplete
          multiple
          freeSolo
          options={tagOptions}
          value={tags}
          onChange={(_e, next) =>
            setTags(Array.from(new Set(next.map((t) => String(t).trim()).filter(Boolean))))
          }
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const { key, ...chipProps } = getTagProps({ index });
              return <Chip key={key} {...chipProps} label={option} size="small" />;
            })
          }
          renderInput={(params) => (
            <TextField {...params} autoFocus label="Tags" placeholder="Type a tag, then Enter" />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={apply}
          disabled={saving || !tags.length}
          sx={{ fontWeight: 900 }}
        >
          {saving ? 'Saving…' : 'Add tags'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
